/*
 * The host half of arrow-key island traversal: pressing an arrow toward a
 * pending suggestion's card opens its island and puts the caret in it.
 *
 * A widget decoration is not a document position, so the host caret can never
 * land "in" a card on its own — without this, an island is reachable only by
 * clicking it, and the keyboard-only path into the review flow does not exist.
 * The direction rules and their exact inverses live in islandTraversal.ts; this
 * extension only supplies the bindings and the things a pure function cannot
 * reach: the proposal set, the island registry, and — for the vertical keys —
 * the view's answer to which visual line a position paints on.
 *
 * Priority 200 puts these ahead of the gap cursor's arrow handling (Tiptap's
 * default 100), which would otherwise claim ArrowLeft/ArrowRight next to the
 * card's block boundaries.
 */
import { Extension } from '@tiptap/core'
import type { EditorView } from '@tiptap/pm/view'
import {
  AI_PROPOSALS_PLUGIN_KEY,
  type AIProposal,
} from '#src/components/rich-text-editor/AIProposalsExtension.ts'
import { getIslandRegistry } from '#src/components/rich-text-editor/islandRegistry.ts'
import {
  type IslandTraversalDirection,
  resolveHorizontalIslandEntry,
  resolveVerticalIslandEntry,
  type SameVisualLine,
} from '#src/components/rich-text-editor/islandTraversal.ts'

export interface IslandTraversalOptions {
  /*
   * The ai-note-suggestions gate, read per keypress rather than baked in. The
   * extension list is memoized for the editor's whole life (rebuilding it tears
   * down the collaboration room), and the flag resolves asynchronously — so the
   * gate has to be a getter or an arrow key would be swallowed into an island
   * mount that the flag-off widget then refuses to render.
   */
  enabled: () => boolean
}

// Entering at the island document's first text position; the end is the
// island's own autofocus default, so it needs no coordinate.
const ISLAND_DOC_START = 1

interface LineBox {
  top: number
  bottom: number
}

/* Which side each direction measures coordinates from: away from the card, so
 * what gets measured is the caret's own text line rather than the card's box.
 * A downward press reads `to` — a position the card ALSO renders at — leftward,
 * which lands on the anchor text; an upward press reads the landing spot past
 * the card rightward, which lands on the text below it. */
const COORD_SIDE: Record<IslandTraversalDirection, number> = {
  forward: -1,
  backward: 1,
}

/* Same line means one box's vertical center falls inside the other. Not box
 * overlap: consecutive line boxes touch (one's bottom is the next one's top),
 * so overlap would read every neighbouring line as the same line — which is
 * precisely the distinction the vertical rule rests on. Centers also keep the
 * comparison honest across a size change, a heading under a paragraph being the
 * common one. */
function onSameLine(a: LineBox, b: LineBox): boolean {
  const centerA = (a.top + a.bottom) / 2
  const centerB = (b.top + b.bottom) / 2
  return (
    (centerA >= b.top && centerA <= b.bottom) ||
    (centerB >= a.top && centerB <= a.bottom)
  )
}

/* jsdom has no layout: every position reports the same zero-size box, so this
 * answers true throughout the unit suite and the structural half of the
 * vertical rule (which textblock, which side of the caret) is what those tests
 * pin. The line half is covered by injecting a probe into the pure resolver. */
function sameVisualLine(
  view: EditorView,
  direction: IslandTraversalDirection
): SameVisualLine {
  const side = COORD_SIDE[direction]
  return (caretPos, boundaryPos) =>
    onSameLine(
      view.coordsAtPos(caretPos, side),
      view.coordsAtPos(boundaryPos, side)
    )
}

export const IslandTraversalExtension =
  Extension.create<IslandTraversalOptions>({
    name: 'aiIslandTraversal',

    priority: 200,

    addOptions() {
      return { enabled: () => true }
    },

    addKeyboardShortcuts() {
      const enter =
        (
          axis: 'horizontal' | 'vertical',
          direction: IslandTraversalDirection
        ) =>
        (): boolean => {
          if (!this.options.enabled()) {
            return false
          }
          const registry = getIslandRegistry(this.editor)
          const proposals = AI_PROPOSALS_PLUGIN_KEY.getState(
            this.editor.state
          )?.proposals
          if (!registry || !proposals) {
            return false
          }
          const candidates = proposals.values() as Iterable<AIProposal>
          const target =
            axis === 'horizontal'
              ? resolveHorizontalIslandEntry(
                  this.editor.state,
                  candidates,
                  direction
                )
              : resolveVerticalIslandEntry(
                  this.editor.state,
                  candidates,
                  direction,
                  sameVisualLine(this.editor.view, direction)
                )
          if (!target) {
            return false
          }
          registry.open(
            target.id,
            target.edge === 'start' ? ISLAND_DOC_START : undefined
          )
          return true
        }
      return {
        ArrowRight: enter('horizontal', 'forward'),
        ArrowDown: enter('vertical', 'forward'),
        ArrowLeft: enter('horizontal', 'backward'),
        ArrowUp: enter('vertical', 'backward'),
      }
    },
  })
